import { MdAlarm, MdHealing, MdMedicalServices, MdStar } from 'react-icons/md';
import type { ReactNode } from 'react';

import {
  APPOINTMENT_REMINDER,
  GOOGLE_REVIEW,
  nomCategorie,
  nomCategorieComplet,
  POST_OP_INSTRUCTIONS,
  PRE_OP_INSTRUCTIONS,
} from '../lib/message-categories';
import { Badge, type BadgeProps } from './badge';

type Apparence = { icone: ReactNode; tone: BadgeProps['tone'] };

const CATEGORIES: Record<string, Apparence> = {
  [APPOINTMENT_REMINDER]: { icone: <MdAlarm />, tone: 'brand' },
  [GOOGLE_REVIEW]: { icone: <MdStar />, tone: 'accent' },
  [PRE_OP_INSTRUCTIONS]: { icone: <MdMedicalServices />, tone: 'neutral' },
  [POST_OP_INSTRUCTIONS]: { icone: <MdHealing />, tone: 'neutral' },
};

/** La catégorie d'un message en pastille : son icône, son nom court, sa teinte. */
export function BadgeCategorie({
  predefiniId,
  compact = false,
  complet = false,
}: {
  predefiniId: string;
  /** Le nom en toutes lettres (« Post-opératoire ») plutôt que le nom court (« Post-op »). */
  complet?: boolean;
  /**
   * L'icône seule, sans son libellé.
   *
   * Pour les cartes du tableau de semaine : une colonne fait dix mots de
   * large, et « Consignes pré-opératoires » y prendrait la place du nom du
   * patient. L'icône suffit à distinguer quatre catégories, et le libellé
   * reste accessible, au survol, et en entier dans l'aperçu.
   */
  compact?: boolean;
}) {
  const apparence = CATEGORIES[predefiniId];
  if (!apparence) return <Badge size="sm">{predefiniId}</Badge>;
  const libelle = complet ? nomCategorieComplet(predefiniId) : nomCategorie(predefiniId);

  if (compact) {
    return (
      <Badge tone={apparence.tone} size="sm" title={libelle} className="px-1.5 py-1 text-sm">
        {apparence.icone}
        <span className="sr-only">{libelle}</span>
      </Badge>
    );
  }

  return (
    <Badge
      tone={apparence.tone}
      size="sm"
      icon={<span className="text-sm">{apparence.icone}</span>}
    >
      {libelle}
    </Badge>
  );
}

/**
 * L'icône de la catégorie, seule : pour les listes qui nomment déjà le
 * modèle, l'icône colorée tient lieu de puce sans répéter le nom.
 */
export function IconeCategorie({ predefiniId }: { predefiniId: string }) {
  return <>{CATEGORIES[predefiniId]?.icone ?? null}</>;
}
